import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { getSpeculativeLoadingBudget } from '@theme/utilities';

const MOBILE_MEDIA = window.matchMedia('(max-width: 749px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const RAIL_SCROLL_END_FALLBACK_MS = 90;

/**
 * Native-first PDP media owner.
 * Browser scrolling/inertia/CSS Scroll Snap own movement; this class owns
 * product-specific state, controls, deferred media, variant replacement and zoom.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {HTMLElement} [slideshow]
 * @property {HTMLElement[]} [media]
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #railActive = false;
  #scrollFrame;
  #scrollEndTimer;
  #pdpVideoObserver;
  #pdpVideoVisibility = new Map();
  #pdpVideoAutoplayPending = new WeakSet();
  #pdpVideoAutoplayBlocked = new WeakSet();
  #pdpVideoAutomaticallyPlaying = new WeakSet();

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#claimNativeControls();
    this.#setupNativeRail();
    this.#setupLazyImageReveal();
    void this.#setupPdpVideoPlayback();
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.isConnected) return;
    this.#claimNativeControls();
    this.#syncRailState();
  }

  disconnectedCallback() {
    this.#deactivateNativeRail();
    for (const image of this.querySelectorAll('img[data-tfp-lazy-pending]')) {
      delete image.dataset.tfpLazyPending;
    }
    this.#pdpVideoObserver?.disconnect();
    this.#pdpVideoObserver = undefined;
    this.#pdpVideoVisibility.clear();
    this.#pauseAllPdpVideos();
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #rail() {
    const rail = this.refs.slideshow;
    return rail instanceof HTMLElement && rail.hasAttribute('data-product-gallery-native') ? rail : null;
  }

  #scroller() {
    const scroller = this.#rail()?.querySelector('[ref="scroller"]');
    return scroller instanceof HTMLElement ? scroller : null;
  }

  get gallerySlides() {
    const scroller = this.#scroller();
    if (!scroller) return [];
    return Array.from(scroller.querySelectorAll(':scope > slideshow-slide')).filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
  }

  #isDesktopGrid() {
    return this.presentation === 'grid' && !MOBILE_MEDIA.matches;
  }

  #claimNativeControls() {
    const rail = this.#rail();
    if (!rail) return;

    for (const control of rail.querySelectorAll('.slideshow-control[on\\:click]')) {
      const action = control.getAttribute('on:click');
      if (!action?.startsWith('/')) continue;

      const method = action.slice(1).split('/')[0];
      if (!['next', 'previous', 'select'].includes(method)) continue;

      control.setAttribute('data-media-gallery-action', action);
      control.removeAttribute('on:click');
    }
  }

  #setupNativeRail() {
    const rail = this.#rail();
    const scroller = this.#scroller();
    if (!rail || !scroller) return;

    this.addEventListener('click', this.#handleNativeControlClick, { signal: this.#controller.signal });
    MOBILE_MEDIA.addEventListener('change', this.#handleBreakpointChange, { signal: this.#controller.signal });

    if (!this.#isDesktopGrid()) this.#activateNativeRail();
    this.#syncRailState(0);
  }

  #activateNativeRail() {
    if (this.#railActive || this.#isDesktopGrid()) return;
    const scroller = this.#scroller();
    if (!scroller) return;

    this.#railActive = true;
    scroller.addEventListener('scroll', this.#handleRailScroll, { passive: true });
    if ('onscrollend' in scroller) {
      scroller.addEventListener('scrollend', this.#handleRailScrollEnd, { passive: true });
    }
  }

  #deactivateNativeRail() {
    const scroller = this.#scroller();
    if (scroller) {
      scroller.removeEventListener('scroll', this.#handleRailScroll);
      scroller.removeEventListener('scrollend', this.#handleRailScrollEnd);
    }
    if (this.#scrollFrame !== undefined) window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;
    window.clearTimeout(this.#scrollEndTimer);
    this.#railActive = false;
  }

  #handleBreakpointChange = () => {
    if (this.#isDesktopGrid()) {
      this.#deactivateNativeRail();
      this.#syncGridAria();
      this.#syncPdpVideoPlayback();
      return;
    }

    this.#activateNativeRail();
    this.#syncRailState();
  };

  #currentRailIndex() {
    const scroller = this.#scroller();
    const slides = this.gallerySlides;
    if (!scroller || !slides.length) return 0;

    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  #syncGridAria() {
    for (const slide of this.gallerySlides) slide.setAttribute('aria-hidden', 'false');
  }

  #syncRailState(index = this.#currentRailIndex()) {
    if (this.#isDesktopGrid()) {
      this.#syncGridAria();
      this.#syncPdpVideoPlayback();
      return;
    }

    const rail = this.#rail();
    const slides = this.gallerySlides;
    if (!rail || !slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === safeIndex ? 'false' : 'true');
    });

    const previous = rail.querySelector('.slideshow-control--previous');
    const next = rail.querySelector('.slideshow-control--next');
    if (previous instanceof HTMLButtonElement) previous.disabled = safeIndex === 0;
    if (next instanceof HTMLButtonElement) next.disabled = safeIndex >= slides.length - 1;

    const counter = rail.querySelector('[ref="current"]');
    if (counter instanceof HTMLElement) counter.textContent = String(safeIndex + 1);

    const progress = this.querySelector('[data-tfp-pdp-gallery-progress]');
    if (progress instanceof HTMLElement) {
      const current = progress.querySelector('[data-tfp-gallery-current]');
      const total = progress.querySelector('[data-tfp-gallery-total]');
      if (current instanceof HTMLElement) current.textContent = String(safeIndex + 1);
      if (total instanceof HTMLElement) total.textContent = String(slides.length);
      progress.style.setProperty('--tfp-gallery-progress', `${((safeIndex + 1) / slides.length) * 100}%`);

      const sizeJump = progress.querySelector('[data-media-gallery-size-picker]');
      if (sizeJump instanceof HTMLButtonElement) {
        sizeJump.hidden = safeIndex === 0 || !this.#sizePickerTarget();
      }
    }

    const dots = Array.from(rail.querySelectorAll('.slideshow-controls__dots .slideshow-control'));
    dots.forEach((dot, dotIndex) => dot.setAttribute('aria-pressed', dotIndex === safeIndex ? 'true' : 'false'));

    const thumbnails = Array.from(rail.querySelectorAll('.slideshow-controls__thumbnail'));
    thumbnails.forEach((thumbnail, thumbnailIndex) =>
      thumbnail.setAttribute('aria-selected', thumbnailIndex === safeIndex ? 'true' : 'false')
    );

    this.#syncPdpVideoPlayback();
  }

  #scrollToIndex(index, { animate = true } = {}) {
    if (this.#isDesktopGrid()) return;

    const scroller = this.#scroller();
    const slides = this.gallerySlides;
    if (!scroller || !slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[safeIndex];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = animate && !reduceMotion ? 'smooth' : 'auto';
    const left = slide.offsetLeft - Math.max(0, (scroller.clientWidth - slide.clientWidth) / 2);

    // Button/dot navigation can move before a pointer event, so prepare the target first.
    this.#warmProductMediaWindow(safeIndex, 1);
    scroller.scrollTo({ left, behavior });
    this.#syncRailState(safeIndex);
  }

  async selectMedia(index, { animate = true } = {}) {
    if (this.#isDesktopGrid()) {
      const media = this.media?.[index];
      media?.scrollIntoView({
        behavior: animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
        block: 'center',
      });
      return;
    }

    this.#activateNativeRail();
    this.#scrollToIndex(index, { animate });
  }

  #handleNativeControlClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const sizePill = event.target.closest('[data-media-gallery-size-picker]');
    if (sizePill instanceof HTMLButtonElement && this.contains(sizePill)) {
      event.preventDefault();
      event.stopPropagation();
      this.#focusCanonicalSizePicker();
      return;
    }

    const control = event.target.closest('[data-media-gallery-action]');
    if (!(control instanceof HTMLElement) || !this.contains(control)) return;

    const action = control.getAttribute('data-media-gallery-action');
    if (!action) return;

    event.preventDefault();
    event.stopPropagation();

    const [method, rawIndex] = action.slice(1).split('/');
    const current = this.#currentRailIndex();

    if (method === 'select') {
      const index = Number.parseInt(rawIndex ?? '', 10);
      if (!Number.isNaN(index)) void this.selectMedia(index);
      return;
    }

    if (method === 'next') void this.selectMedia(current + 1);
    if (method === 'previous') void this.selectMedia(current - 1);
  };

  #sizePickerTarget() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector(
      "variant-picker[data-template-product-match='true'][data-market-size-product='true']"
    );
    if (!(picker instanceof HTMLElement)) return null;

    const sizeInput = Array.from(picker.querySelectorAll('input[data-option-name]')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      const name = (input.dataset.optionName || '').toLocaleLowerCase(document.documentElement.lang || undefined);
      return name.includes('size') || name.includes('μέγεθ') || name.includes('νούμερ');
    });
    return sizeInput?.closest('fieldset') || null;
  }

  #focusCanonicalSizePicker() {
    const target = this.#sizePickerTarget();
    if (!(target instanceof HTMLElement)) return;

    const reduceMotion = REDUCED_MOTION.matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    if (reduceMotion || typeof target.animate !== 'function') return;
    for (const animation of target.getAnimations()) {
      if (animation.id === 'tfp-size-picker-arrival') animation.cancel();
    }
    const animation = target.animate(
      [{ opacity: 0.82, transform: 'scale(0.99)' }, { opacity: 1, transform: 'scale(1)' }],
      { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    animation.id = 'tfp-size-picker-arrival';
  }

  #handleRailScroll = () => {
    const scroller = this.#scroller();
    if (!scroller) return;

    if (this.#scrollFrame === undefined) {
      this.#scrollFrame = window.requestAnimationFrame(() => {
        this.#scrollFrame = undefined;
        const index = this.#currentRailIndex();
        this.#syncRailState(index);
      });
    }

    if ('onscrollend' in scroller) return;

    window.clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = window.setTimeout(this.#handleRailScrollEnd, RAIL_SCROLL_END_FALLBACK_MS);
  };

  #handleRailScrollEnd = () => {
    if (this.#scrollFrame !== undefined) window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;
    const index = this.#currentRailIndex();
    this.#syncRailState(index);
  };

  #warmProductMediaWindow(startIndex, count) {
    const slides = this.gallerySlides;
    if (!slides.length || count <= 0) return;

    const start = Math.max(0, startIndex);
    const end = Math.min(slides.length, start + count);

    for (let index = start; index < end; index += 1) {
      const image = slides[index]?.querySelector('img.product-media__image');
      if (!(image instanceof HTMLImageElement) || image.complete) continue;
      if (index === 0 || image.fetchPriority === 'high') continue;

      // Keep a real src/srcset in HTML at all times. JS only promotes priority.
      image.fetchPriority = 'low';
      if (image.loading === 'lazy') image.loading = 'eager';
    }
  }

  #setupLazyImageReveal() {
    if (REDUCED_MOTION.matches) return;

    const images = this.querySelectorAll('img.product-media__image[loading="lazy"]:not([fetchpriority="high"])');
    const { signal } = this.#controller;
    for (const image of images) {
      if (image.complete) {
        delete image.dataset.tfpLazyPending;
        continue;
      }

      image.dataset.tfpLazyPending = '';
      const settle = () => {
        const reveal = () => {
          if (this.isConnected && image.isConnected) delete image.dataset.tfpLazyPending;
        };
        if (image.naturalWidth && typeof image.decode === 'function') {
          void image.decode().catch(() => {}).then(reveal);
        } else {
          reveal();
        }
      };
      image.addEventListener('load', settle, { once: true, signal });
      image.addEventListener('error', settle, { once: true, signal });
    }
  }

  #pdpVideos() {
    return Array.from(this.querySelectorAll('deferred-media[data-product-video]'));
  }

  async #setupPdpVideoPlayback() {
    const videos = this.#pdpVideos();
    if (videos.length === 0) return;

    const controller = this.#controller;
    await customElements.whenDefined('deferred-media');
    if (!this.isConnected || controller.signal.aborted || controller !== this.#controller) return;

    const { signal } = controller;
    for (const video of videos) this.#pdpVideoVisibility.set(video, 0);

    if ('IntersectionObserver' in window) {
      this.#pdpVideoObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#pdpVideoVisibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
          }
          this.#syncPdpVideoPlayback();
        },
        { threshold: [0, 0.6, 1] }
      );

      for (const video of videos) this.#pdpVideoObserver.observe(video);
    } else {
      window.addEventListener('scroll', this.#measurePdpVideoVisibility, { passive: true, signal });
      window.addEventListener('resize', this.#measurePdpVideoVisibility, { passive: true, signal });
      queueMicrotask(this.#measurePdpVideoVisibility);
    }

    document.addEventListener('visibilitychange', this.#syncPdpVideoPlayback, { signal });
    REDUCED_MOTION.addEventListener('change', this.#syncPdpVideoPlayback, { signal });

    const connection = /** @type {any} */ (
      navigator.connection || navigator.mozConnection || navigator.webkitConnection
    );
    connection?.addEventListener?.('change', this.#syncPdpVideoPlayback, { signal });
  }

  #measurePdpVideoVisibility = () => {
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    for (const video of this.#pdpVideos()) {
      const rect = video.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const area = rect.width * rect.height;
      this.#pdpVideoVisibility.set(video, area > 0 ? (visibleWidth * visibleHeight) / area : 0);
    }

    this.#syncPdpVideoPlayback();
  };

  #activePdpVideo() {
    if (document.hidden) return null;

    let activeVideo = null;
    let activeRatio = 0;
    for (const [video, ratio] of this.#pdpVideoVisibility) {
      const slide = video.closest('slideshow-slide');
      if (slide instanceof HTMLElement && slide.getAttribute('aria-hidden') === 'true') continue;
      if (ratio < 0.6) continue;
      if (ratio > activeRatio) {
        activeVideo = video;
        activeRatio = ratio;
      }
    }

    return activeVideo;
  }

  #allowsPdpVideoAutoplay() {
    return !REDUCED_MOTION.matches && getSpeculativeLoadingBudget() !== 'none';
  }

  #pausePdpVideo(video) {
    video.pauseMedia?.();
    this.#pdpVideoAutomaticallyPlaying.delete(video);
  }

  #pauseAllPdpVideos() {
    for (const video of this.#pdpVideos()) this.#pausePdpVideo(video);
  }

  #syncPdpVideoPlayback = () => {
    const activeVideo = this.#activePdpVideo();

    for (const video of this.#pdpVideos()) {
      if (video !== activeVideo) this.#pausePdpVideo(video);
    }

    if (!activeVideo) return;

    if (!this.#allowsPdpVideoAutoplay()) {
      if (this.#pdpVideoAutomaticallyPlaying.has(activeVideo)) this.#pausePdpVideo(activeVideo);
      return;
    }

    if (
      activeVideo.isPlaying ||
      activeVideo.hasAttribute('data-user-paused') ||
      this.#pdpVideoAutoplayPending.has(activeVideo) ||
      this.#pdpVideoAutoplayBlocked.has(activeVideo)
    ) {
      return;
    }

    this.#pdpVideoAutoplayPending.add(activeVideo);
    Promise.resolve(activeVideo.playMedia?.())
      .then((played) => {
        if (!played) {
          this.#pdpVideoAutoplayBlocked.add(activeVideo);
          return;
        }

        if (activeVideo !== this.#activePdpVideo() || !this.#allowsPdpVideoAutoplay()) {
          this.#pausePdpVideo(activeVideo);
          return;
        }

        this.#pdpVideoAutomaticallyPlaying.add(activeVideo);
      })
      .catch(() => this.#pdpVideoAutoplayBlocked.add(activeVideo))
      .finally(() => this.#pdpVideoAutoplayPending.delete(activeVideo));
  };

  #mediaRenderSignature(root) {
    return Array.from(root.querySelectorAll('slideshow-slide'), (slide) => {
      const media = slide.querySelector('.product-media[data-media-id]');
      if (!(media instanceof HTMLElement)) return '';

      return `${media.dataset.mediaId ?? ''}:${slide.hasAttribute('hidden') ? 'hidden' : 'visible'}`;
    }).filter(Boolean);
  }

  #canPreserveGallery(newMediaGallery, detail) {
    if (detail?.newProduct) return false;
    if (this.dataset.presentation !== newMediaGallery.dataset.presentation) return false;

    const currentSignature = this.#mediaRenderSignature(this);
    const nextSignature = this.#mediaRenderSignature(newMediaGallery);

    return (
      currentSignature.length > 0 &&
      currentSignature.length === nextSignature.length &&
      currentSignature.every((value, index) => value === nextSignature[index])
    );
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!(newMediaGallery instanceof HTMLElement)) return;

        // Size-only variant changes commonly return identical media markup.
        // Preserve the existing native gallery so it keeps scroll position, decoded
        // images, observers and listeners instead of rebuilding the same component.
        if (this.#canPreserveGallery(newMediaGallery, detail)) {
          const currentSizePill = this.querySelector('[data-media-gallery-size-picker]');
          const nextSizePill = newMediaGallery.querySelector('[data-media-gallery-size-picker]');
          if (currentSizePill instanceof HTMLButtonElement && nextSizePill instanceof HTMLButtonElement) {
            currentSizePill.setAttribute('aria-label', nextSizePill.getAttribute('aria-label') || '');
          }
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    await this.selectMedia(event.detail.index, { animate: false });
  };

  async zoom(index, event) {
    await this.#ensureZoomRuntime();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  #zoomDialogRuntime;
  #dragZoomRuntime;

  #ensureZoomDialogRuntime() {
    this.#zoomDialogRuntime ||= import('@theme/zoom-dialog');
    return this.#zoomDialogRuntime;
  }

  async #ensureZoomRuntime() {
    this.#dragZoomRuntime ||= Promise.all([
      this.#ensureZoomDialogRuntime(),
      import('@theme/drag-zoom-wrapper'),
    ]);
    await this.#dragZoomRuntime;
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
